import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardMedia, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import { paths } from '../config/paths';
import { v2Colors, v2DisplayFontFamily } from '../design/theme';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { fetchBlogPosts, logBlogListView } from '../services/blogs';
import { formatBlogDate, getMetaKeywords, getPostTitle, getReadingTime, getSnippet } from '../utils/blog';

export default function Blogs() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const posts = await fetchBlogPosts();
      setBlogPosts(Array.isArray(posts) ? posts : []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    logBlogListView();
    loadPosts();
  }, [loadPosts]);

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4, maxWidth: { xs: 720, md: 960 } }}>
      <Helmet>
        <title>Blog | Country Chicken Co</title>
        <meta
          name="description"
          content="Discover articles about country chicken, sustainable farming, health benefits of free-range poultry, and delicious recipes."
        />
        {blogPosts.length > 0 ? <meta name="keywords" content={getMetaKeywords(blogPosts)} /> : null}
      </Helmet>

      <CheckoutPageHeader backTo={paths.home} title="Blogs" />

      <Typography
        sx={{
          mb: 3,
          color: v2Colors.textMuted,
          fontSize: { xs: '0.95rem', md: '1.05rem' },
          lineHeight: 1.6,
        }}
      >
        Welcome to the Country Chicken Co blogs! Explore insights on naturally raised meats, healthy eating, and
        farm-fresh recipes.
      </Typography>

      <PageState loading={loading} error={error} onRetry={loadPosts} errorTitle="Could not load blogs">
        {blogPosts.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: v2Colors.textMuted }}>
            No blog posts available yet. Check back soon!
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            {blogPosts.map((post) => {
              const title = getPostTitle(post);
              return (
                <Card
                  key={post.id}
                  component={RouterLink}
                  to={paths.blogPost(post.urlId)}
                  elevation={0}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: v2Colors.borderMuted,
                    bgcolor: v2Colors.white,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(90, 60, 50, 0.12)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={post.coverImage || noImage}
                    alt={title}
                    sx={{ objectFit: 'cover', height: 200, bgcolor: v2Colors.surface }}
                  />
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: v2Colors.textMuted, mb: 0.75 }}>
                      {formatBlogDate(post.createdAt)}
                      {post.content ? ` · ${getReadingTime(post.content)}` : ''}
                    </Typography>
                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: v2DisplayFontFamily,
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        lineHeight: 1.25,
                        color: v2Colors.heading,
                        mb: 1,
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: v2Colors.textMuted, lineHeight: 1.55 }}>
                      {getSnippet(post)}
                    </Typography>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </PageState>
    </PageContainer>
  );
}
